import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  LeadRestService,
  MasterZoneRestService,
  OfficeListItemDto,
  OfficeRestService,
  OfficeStatus,
  UserRestService,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { ColumnEnableConditions, GridLayout, GridLayoutService } from '@gh/core-ui-grid';
import {
  BuilderUserContext,
  DEFAULT_FILTER_STATE,
  FilterNames,
  FilterRestService,
  parseLeadsListFilterFromRoute,
  peekLeadsListPageReload,
  resolveCorpOfficeSelection,
  resolveLeadsListFields,
  toCorpOfficeFilterOptions,
} from '@gh/builder-shared';
import { forkJoin, Observable } from 'rxjs';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { map$, shareReplay$, switchMap$ } from '@gh/rx/operators';
import { combineLatest$, of$ } from '@gh/rx';
import { sortByStringField } from '@gh/core-util';
import { MasterZone } from '../../builder-shared/switch-to/resources/master-zone.model';
import { Office } from '../../builder-shared/switch-to/resources/office.model';
import { LeadListItem } from '../../builder-sales/leads/resources/lead.model';

export type UserDetailsShort = {
  id: ID;
  name: string;
  isInactive?: boolean;
};

@Injectable()
export class LeadListBusinessService implements InitViewService {

  COLUMN_ENABLE_CONDITION = {
    masterZoneName: (): boolean => this.userContext.corporateView,
    officeName: (): boolean => this.userContext.corporateView || this.userContext.masterView,
  } as ColumnEnableConditions;

  constructor(private userContext: BuilderUserContext,
              private masterZoneRest: MasterZoneRestService,
              private officeRest: OfficeRestService,
              private leadRest: LeadRestService,
              private userRest: UserRestService,
              private dictionaries: DictionaryService,
              private filterService: FilterRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const filterName = this.userContext.corporateView ? FilterNames.leadsListCorp : FilterNames.leadsListMaster;
    const pageReload = peekLeadsListPageReload();
    const routeFilter = pageReload ? null : parseLeadsListFilterFromRoute(route);
    const masterZonesSub = this.userContext.corporateView
      ? this.findAllMasterZones().pipe(shareReplay$({ bufferSize: 1, refCount: true }))
      : of$([this.userContext.masterZone]);
    const gridLayoutName = this.userContext.corporateView ? 'leadsListCorp' : 'leadsListMaster';
    const gridLayoutSub = this.gridLayoutService.get(gridLayoutName, this.COLUMN_ENABLE_CONDITION);

    return this.filterService.get(filterName).pipe(
      switchMap$((savedFilter: any) => {
        const defaultFilter = { ...DEFAULT_FILTER_STATE[filterName] };

        // Only restore Master Zone and Office from saved filter
        const mergedFilter = {
          ...defaultFilter,
          masterZoneId: savedFilter?.masterZoneId || '',
          officeId: savedFilter?.officeId || [''],
          ...routeFilter,
          showSuppressed: false,
        };

        let masterZoneId = '';
        if (this.userContext.masterView && this.userContext.masterZone) {
          masterZoneId = this.userContext.masterZone.id;
          mergedFilter.masterZoneId = masterZoneId;
          mergedFilter.masterZone = this.userContext.masterZone;
        } else if (this.userContext.corporateView) {
          if (pageReload) {
            masterZoneId = '';
            mergedFilter.masterZoneId = '';
            mergedFilter.officeId = [''];
          } else {
            masterZoneId = mergedFilter.masterZoneId || mergedFilter.masterZone?.id || '';
          }
        }

        let salesPersonsSub: Observable<any>;
        const salesPersonSource = mergedFilter;
        if (Array.isArray(salesPersonSource.salesPersonId)) {
          salesPersonsSub = salesPersonSource.salesPersonId.length > 0 && salesPersonSource.salesPersonId[0] !== ''
            ? forkJoin(salesPersonSource.salesPersonId.map((id: ID) => id === '' ? of$({name: 'All', id: ''}) : this.getSalesPerson(id)))
            : of$([{name: 'All', id: ''}]);
        } else {
          salesPersonsSub = (salesPersonSource.salesPersonId && salesPersonSource.salesPersonId !== '')
            ? this.getSalesPerson(salesPersonSource.salesPersonId)
            : of$([{name: 'All', id: ''}]);
        }

        const officesSub = this.userContext.corporateView
          ? (
              masterZoneId
                ? this.findOfficesForCorpLeadsFilter(masterZoneId)
                : masterZonesSub.pipe(
                    switchMap$((zones: MasterZone[]) => {
                      const knownIds = (zones || [])
                        .map((zone) => zone.id)
                        .filter((id) => id != null && String(id) !== '') as ID[];
                      return this.findOfficesForCorpLeadsFilter(masterZoneId, knownIds);
                    }),
                  )
            ).pipe(map$((loaded) => toCorpOfficeFilterOptions(loaded)))
          : (masterZoneId ? this.findOfficesByMasterName(masterZoneId) : of$([{id: '', name: 'All'} as Office]));

        return combineLatest$([
          masterZonesSub,
          gridLayoutSub,
          officesSub,
          salesPersonsSub,
          this.dictionaries.getDictionaryFromGlobal('SALES_FOLDER_STATUS'),
        ]).pipe(map$(([masterZones, gridLayout, offices, salesPersons, salesFolderStatuses]) => {
          if (this.userContext.corporateView) {
            mergedFilter.officeId = resolveCorpOfficeSelection(offices, mergedFilter.officeId);
          } else if (mergedFilter.officeId) {
            const officeIds = Array.isArray(mergedFilter.officeId) ? mergedFilter.officeId : [mergedFilter.officeId];
            const validOfficeIds = officeIds.filter((id: string) => id === '' || offices.some((o) => o.id === id));
            mergedFilter.officeId = validOfficeIds.length > 0 ? validOfficeIds : [''];
          }
          return {
            masterZones,
            gridLayout,
            offices,
            salesPersons,
            filter: mergedFilter,
            salesFolderStatuses,
          };
        }));
      }),
    );
  }

  findAllMasterZones(): Observable<MasterZone[]> {
    return this.masterZoneRest.findAll();
  }

  findAllAsDataSource(filter: any, layout?: GridLayout): SlicedDataSource<LeadListItem> {
    return {
      select: (query) => {
        const fields = resolveLeadsListFields(layout, filter || {}, query.sorting?.field);
        return this.leadRest.findAllAsDataSource({ ...filter, fields }).select(query);
      },
    };
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, filter: any): Observable<UserDetailsShort[]> {
    return this.userRest.findSalespersonsAsDataSource({
      ...filter,
      searchString: query.filter,
    }).select(query).pipe(map$(getSlicedData), map$((res: any[]) => res.map((user) => ({
      id: user.id || user.userId,
      name: user.name || user.fullName || user.userName || user.contact?.name || user.userEmail || 'Unknown',
      isInactive: !!(user.status && user.status.toString().toUpperCase() !== 'ACTIVE')
    }))));
  }

  findOfficesByMasterName(masterId: ID, name?: string): Observable<Office[]> {
    return this.officeRest.findAllByMasterZone(masterId, ['deactivated', 'active'])
      .pipe(map$((res) => name ? sortByStringField(res.filter((office) => office.name.toLowerCase()
            .includes(name.toLowerCase())),        'status')
          : [{id: '', name: 'All'} as Office, ...sortByStringField(res, 'status')]));
  }

  findOfficesForCorpLeadsFilter(masterZoneId?: ID, knownMasterZoneIds?: ID[]): Observable<Office[]> {
    if (masterZoneId) {
      return this.findOfficesByMasterName(masterZoneId);
    }
    // undefined = caller does not have ids yet; [] = caller already resolved and there are none (do not refetch).
    const zoneIdsSub = knownMasterZoneIds != null
      ? of$((knownMasterZoneIds || []).filter((id) => id != null && String(id) !== '') as ID[])
      : this.findAllMasterZones().pipe(
          map$((zones: MasterZone[]) =>
            (zones || [])
              .map((zone) => zone.id)
              .filter((id) => id != null && String(id) !== '') as ID[]),
        );
    return zoneIdsSub.pipe(
      switchMap$((ids: ID[]) => {
        if (ids.length === 0) {
          return of$([] as Office[]);
        }
        const ds = this.officeRest.findAllForFinderAsDataSource({
          masterZoneIds: ids,
          statuses: [OfficeStatus.Active, OfficeStatus.Deactivated],
          name: '',
          types: ['FRANCHISE', 'TEMPLATE'],
        });
        const query: FilteredDataQuery<string> = { from: 0, to: 10000, filter: '' };
        return ds.select(query).pipe(
          map$(getSlicedData),
          map$((rows: OfficeListItemDto[]) =>
            sortByStringField(
              rows.map((row) => ({
                id: row.officeId,
                name: row.name,
                status: row.statusValue || row.status,
              } as Office)),
              'name',
            )),
        );
      }),
    );
  }

  findAllActiveOfficesAcrossMasterZones(): Observable<Office[]> {
    return this.findAllMasterZones().pipe(
      switchMap$((zones: MasterZone[]) => {
        const ids = (zones || [])
          .map((z) => z.id)
          .filter((id) => id != null && String(id) !== '') as ID[];
        if (ids.length === 0) {
          return of$([]);
        }
        const ds = this.officeRest.findAllForFinderAsDataSource({
          masterZoneIds: ids,
          statuses: [OfficeStatus.Active],
          name: '',
          types: ['FRANCHISE'],
        });
        const query: FilteredDataQuery<string> = { from: 0, to: 10000, filter: '' };
        return ds.select(query).pipe(
          map$(getSlicedData),
          map$((rows: OfficeListItemDto[]) =>
            sortByStringField(
              rows.map((row) => ({ id: row.officeId, name: row.name } as Office)),
              'name',
            )),
        );
      }),
    );
  }

  getSalesPerson(id: ID): Observable<UserDetailsShort> {
    return this.userRest.get(id).pipe(map$((user) => ({
      id: user.userId!,
      name: user.contact?.name || 'Unknown',
      isInactive: !!(user.status && user.status.toString().toUpperCase() !== 'ACTIVE')
    })));
  }

  bulkSuppress(leads: { contactId: ID, salesFolderId: ID }[]): Observable<void> {
    return this.leadRest.bulkSuppress(leads);
  }

  bulkUnsuppress(leads: { contactId: ID, salesFolderId: ID }[]): Observable<void> {
    return this.leadRest.bulkUnsuppress(leads);
  }
}